const SHOPIFY_URL = `https://${process.env.REACT_APP_SHOPIFY_DOMAIN}/api/2024-01/graphql.json`;
const SHOPIFY_TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;

async function shopifyFetch(query, variables = {}) {
  const res = await fetch(SHOPIFY_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': SHOPIFY_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });
  const { data, errors } = await res.json();
  if (errors) throw new Error(errors[0].message);
  return data;
}

const PRODUCT_FIELDS = `
  handle
  title
  productType
  description
  availableForSale
  options {
    name
    values
  }
  images(first: 3) {
    edges { node { url } }
  }
  variants(first: 50) {
    edges {
      node {
        id
        title
        priceV2 { amount currencyCode }
        selectedOptions { name value }
      }
    }
  }
`;

const CART_LINE_FIELDS = `
  id
  quantity
  merchandise {
    ... on ProductVariant {
      id
      title
      priceV2 { amount }
      selectedOptions { name value }
      product {
        handle
        title
        images(first: 3) {
          edges { node { url } }
        }
      }
    }
  }
`;

function normalizeProduct(node) {
  const variants = node.variants.edges.map(e => e.node);
  const price = parseFloat(variants[0]?.priceV2?.amount ?? 0);

  const materialOption = node.options.find(o => o.name.toLowerCase() === 'material');
  const materials = materialOption ? materialOption.values : (node.options[0]?.values ?? []);

  const variantsByMaterial = {};
  variants.forEach(v => {
    const mat = v.selectedOptions.find(o => o.name.toLowerCase() === 'material');
    const key = mat ? mat.value : v.title;
    variantsByMaterial[key] = v.id;
  });

  return {
    id: node.handle,
    title: node.title,
    type: node.productType,
    productdesc: node.description,
    price,
    images: node.images.edges.map(e => e.node.url),
    materials,
    quantity: node.availableForSale ? 1 : 0,
    alt: node.title,
    variantsByMaterial,
  };
}

export async function getProducts() {
  const data = await shopifyFetch(`
    query {
      products(first: 100, sortKey: ID) {
        edges { node { ${PRODUCT_FIELDS} } }
      }
    }
  `);
  return data.products.edges.map(e => normalizeProduct(e.node));
}

export async function getLandingProducts() {
  const data = await shopifyFetch(`
    query {
      products(first: 3, sortKey: ID) {
        edges { node { ${PRODUCT_FIELDS} } }
      }
    }
  `);
  return data.products.edges.map(e => normalizeProduct(e.node));
}

export async function getProductByHandle(handle) {
  const data = await shopifyFetch(`
    query getProduct($handle: String!) {
      productByHandle(handle: $handle) { ${PRODUCT_FIELDS} }
    }
  `, { handle });
  if (!data.productByHandle) return null;
  return normalizeProduct(data.productByHandle);
}

export function normalizeCartLines(cart) {
  return (cart?.lines?.edges ?? []).map(({ node: line }) => {
    const variant = line.merchandise;
    const materialOption = variant.selectedOptions.find(
      o => o.name.toLowerCase() === 'material'
    );
    const material = materialOption?.value ?? variant.title;
    return {
      cart_item_id: line.id,
      product_id: variant.product.handle,
      quantity: line.quantity,
      material,
      title: variant.product.title,
      price: parseFloat(variant.priceV2.amount),
      images: variant.product.images.edges.map(e => e.node.url),
    };
  });
}

export async function createShopifyCart(lines = []) {
  const data = await shopifyFetch(`
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart { id checkoutUrl lines(first: 50) { edges { node { ${CART_LINE_FIELDS} } } } }
        userErrors { field message }
      }
    }
  `, { input: { lines } });
  if (data.cartCreate.userErrors.length) throw new Error(data.cartCreate.userErrors[0].message);
  return data.cartCreate.cart;
}

export async function addCartLines(cartId, lines) {
  const data = await shopifyFetch(`
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart { id checkoutUrl lines(first: 50) { edges { node { ${CART_LINE_FIELDS} } } } }
        userErrors { field message }
      }
    }
  `, { cartId, lines });
  if (data.cartLinesAdd.userErrors.length) throw new Error(data.cartLinesAdd.userErrors[0].message);
  return data.cartLinesAdd.cart;
}

export async function removeCartLines(cartId, lineIds) {
  const data = await shopifyFetch(`
    mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart { id checkoutUrl lines(first: 50) { edges { node { ${CART_LINE_FIELDS} } } } }
        userErrors { field message }
      }
    }
  `, { cartId, lineIds });
  if (data.cartLinesRemove.userErrors.length) throw new Error(data.cartLinesRemove.userErrors[0].message);
  return data.cartLinesRemove.cart;
}

export async function fetchShopifyCart(cartId) {
  const data = await shopifyFetch(`
    query getCart($cartId: ID!) {
      cart(id: $cartId) {
        id
        checkoutUrl
        lines(first: 50) { edges { node { ${CART_LINE_FIELDS} } } }
      }
    }
  `, { cartId });
  return data.cart;
}

// ─── Customer API ────────────────────────────────────────────────────────────

function normalizeCustomer(c) {
  return {
    id: c.id,
    first_name: c.firstName,
    last_name: c.lastName,
    email: c.email,
    address: c.defaultAddress?.address1 ?? '',
    defaultAddressId: c.defaultAddress?.id ?? null,
    orders: (c.orders?.edges ?? []).map(({ node: o }) => ({
      id: o.orderNumber,
      status: o.fulfillmentStatus,
      financialStatus: o.financialStatus,
      items: o.lineItems.edges,
      total: parseFloat(o.totalPriceV2.amount),
      currency: o.totalPriceV2.currencyCode,
      processedAt: o.processedAt,
    })),
  };
}

export async function shopifyCustomerCreate(firstName, lastName, email, password) {
  const data = await shopifyFetch(`
    mutation customerCreate($input: CustomerCreateInput!) {
      customerCreate(input: $input) {
        customer { id email firstName lastName }
        customerUserErrors { code field message }
      }
    }
  `, { input: { firstName, lastName, email, password } });
  const { customerUserErrors } = data.customerCreate;
  if (customerUserErrors.length) throw new Error(customerUserErrors[0].message);
  return data.customerCreate.customer;
}

export async function shopifyCustomerLogin(email, password) {
  const data = await shopifyFetch(`
    mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
      customerAccessTokenCreate(input: $input) {
        customerAccessToken { accessToken expiresAt }
        customerUserErrors { code field message }
      }
    }
  `, { input: { email, password } });
  const { customerAccessToken, customerUserErrors } = data.customerAccessTokenCreate;
  if (customerUserErrors.length) throw new Error(customerUserErrors[0].message);
  if (!customerAccessToken) throw new Error('Invalid credentials');
  return customerAccessToken.accessToken;
}

export async function shopifyCustomerLogout(accessToken) {
  await shopifyFetch(`
    mutation customerAccessTokenDelete($customerAccessToken: String!) {
      customerAccessTokenDelete(customerAccessToken: $customerAccessToken) {
        deletedAccessToken
        userErrors { field message }
      }
    }
  `, { customerAccessToken: accessToken });
}

export async function shopifyGetCustomer(accessToken) {
  const data = await shopifyFetch(`
    query getCustomer($customerAccessToken: String!) {
      customer(customerAccessToken: $customerAccessToken) {
        id firstName lastName email
        defaultAddress { id address1 city province zip country }
        orders(first: 10, sortKey: PROCESSED_AT, reverse: true) {
          edges {
            node {
              orderNumber
              totalPriceV2 { amount currencyCode }
              financialStatus
              fulfillmentStatus
              processedAt
              lineItems(first: 10) {
                edges { node { title quantity } }
              }
            }
          }
        }
      }
    }
  `, { customerAccessToken: accessToken });
  if (!data.customer) return null;
  return normalizeCustomer(data.customer);
}

export async function shopifyCustomerUpdate(accessToken, fields) {
  const data = await shopifyFetch(`
    mutation customerUpdate($customerAccessToken: String!, $customer: CustomerUpdateInput!) {
      customerUpdate(customerAccessToken: $customerAccessToken, customer: $customer) {
        customer { id email firstName lastName }
        customerUserErrors { code field message }
      }
    }
  `, { customerAccessToken: accessToken, customer: fields });
  const { customerUserErrors } = data.customerUpdate;
  if (customerUserErrors.length) throw new Error(customerUserErrors[0].message);
  return data.customerUpdate.customer;
}

export async function shopifyCustomerAddressUpdate(accessToken, addressId, address) {
  const data = await shopifyFetch(`
    mutation customerAddressUpdate($customerAccessToken: String!, $id: ID!, $address: MailingAddressInput!) {
      customerAddressUpdate(customerAccessToken: $customerAccessToken, id: $id, address: $address) {
        customerAddress { id address1 }
        customerUserErrors { code field message }
      }
    }
  `, { customerAccessToken: accessToken, id: addressId, address });
  const { customerUserErrors } = data.customerAddressUpdate;
  if (customerUserErrors.length) throw new Error(customerUserErrors[0].message);
}

export async function shopifyCustomerRecover(email) {
  const data = await shopifyFetch(`
    mutation customerRecover($email: String!) {
      customerRecover(email: $email) {
        customerUserErrors { code field message }
      }
    }
  `, { email });
  const { customerUserErrors } = data.customerRecover;
  if (customerUserErrors.length) throw new Error(customerUserErrors[0].message);
}

export async function shopifyCustomerAddressCreate(accessToken, address) {
  const data = await shopifyFetch(`
    mutation customerAddressCreate($customerAccessToken: String!, $address: MailingAddressInput!) {
      customerAddressCreate(customerAccessToken: $customerAccessToken, address: $address) {
        customerAddress { id address1 }
        customerUserErrors { code field message }
      }
    }
  `, { customerAccessToken: accessToken, address });
  const { customerAddress, customerUserErrors } = data.customerAddressCreate;
  if (customerUserErrors.length) throw new Error(customerUserErrors[0].message);

  // Set newly created address as default so it shows up in the account panel
  if (customerAddress?.id) {
    await shopifyFetch(`
      mutation customerDefaultAddressUpdate($customerAccessToken: String!, $addressId: ID!) {
        customerDefaultAddressUpdate(customerAccessToken: $customerAccessToken, addressId: $addressId) {
          customerUserErrors { code field message }
        }
      }
    `, { customerAccessToken: accessToken, addressId: customerAddress.id });
  }
}
